/**
 * Notification bell + dropdown inbox in the shell.
 * Panel is portaled to document.body with fixed coords so the sidebar
 * footer (overflow/narrow width) cannot clip it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Bell, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
  useDeleteNotification,
  useClearAllNotifications,
} from '../api.js';
import { formatDateTime } from '../../../lib/format.js';

const PANEL_WIDTH = 320; // w-80
const PANEL_MAX_HEIGHT = 320; // max-h-80
const GAP = 8;
const VIEWPORT_PAD = 8;
function sidebarWidthPx(): number {
  return document.querySelector('aside.app-sidebar')?.getBoundingClientRect().width ?? 0;
}

type PanelCoords = {
  top: number;
  left: number;
  maxHeight: number;
};

function computePanelCoords(anchor: DOMRect): PanelCoords {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const SIDEBAR_WIDTH = sidebarWidthPx();

  // Desktop sidebar footer: open into the main content area to the right of
  // the sidebar so the panel never covers nav items.
  const inSidebarFooter = viewportW >= 768 && anchor.left < Math.max(SIDEBAR_WIDTH, 80);

  if (inSidebarFooter) {
    const maxHeight = Math.min(PANEL_MAX_HEIGHT, viewportH - 2 * VIEWPORT_PAD);
    // Bottom-align with the bell, then clamp so we stay on-screen.
    let top = anchor.bottom - maxHeight;
    top = Math.max(VIEWPORT_PAD, Math.min(top, viewportH - VIEWPORT_PAD - 120));
    const height = Math.min(maxHeight, viewportH - top - VIEWPORT_PAD);

    let left = SIDEBAR_WIDTH + GAP;
    left = Math.min(left, viewportW - PANEL_WIDTH - VIEWPORT_PAD);
    left = Math.max(VIEWPORT_PAD, left);

    return { top, left, maxHeight: height };
  }

  // Mobile / top-header bell: open below when possible, otherwise above.
  const spaceBelow = viewportH - anchor.bottom - VIEWPORT_PAD;
  const spaceAbove = anchor.top - VIEWPORT_PAD;
  const openBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;

  const available = Math.max(120, openBelow ? spaceBelow - GAP : spaceAbove - GAP);
  const maxHeight = Math.min(PANEL_MAX_HEIGHT, available);

  let top = openBelow ? anchor.bottom + GAP : anchor.top - GAP - maxHeight;
  top = Math.min(Math.max(VIEWPORT_PAD, top), viewportH - VIEWPORT_PAD - Math.min(maxHeight, available));

  // Right-align to the bell, then clamp horizontally.
  let left = anchor.right - PANEL_WIDTH;
  left = Math.min(Math.max(VIEWPORT_PAD, left), viewportW - PANEL_WIDTH - VIEWPORT_PAD);

  return { top, left, maxHeight };
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PanelCoords | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: unread = 0 } = useUnreadNotificationCount();
  const { data, isLoading } = useNotifications({ limit: 15 });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const clearOne = useDeleteNotification();
  const clearAll = useClearAllNotifications();

  const updateCoords = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords(computePanelCoords(rect));
  };

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => updateCoords();

    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const items = data?.items ?? [];

  const panel =
    open && coords
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            className="fixed z-[100] w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
            style={{
              top: coords.top,
              left: coords.left,
              maxHeight: coords.maxHeight,
            }}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-800">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Notifications</p>
              <div className="flex items-center gap-3">
                {unread > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400"
                    onClick={() => markAll.mutate()}
                    disabled={markAll.isPending}
                  >
                    Mark all read
                  </button>
                )}
                {items.length > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => clearAll.mutate()}
                    disabled={clearAll.isPending}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: Math.max(80, coords.maxHeight - 44) }}>
              {isLoading ? (
                <p className="px-3 py-6 text-center text-sm text-slate-400">Loading…</p>
              ) : items.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-slate-400">You are all caught up.</p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((n) => (
                    <li key={n.id} className="relative">
                      <button
                        type="button"
                        className={clsx(
                          'block w-full px-3 py-2.5 pr-9 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60',
                          n.readAt === null && 'bg-sky-50/60 dark:bg-sky-950/30',
                        )}
                        onClick={() => {
                          if (n.readAt === null) markRead.mutate(n.id);
                          setOpen(false);
                          if (n.href) navigate(n.href);
                        }}
                      >
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{n.title}</p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{n.body}</p>
                        <p className="mt-1 text-[11px] text-slate-400">{formatDateTime(n.createdAt)}</p>
                      </button>
                      <button
                        type="button"
                        aria-label={`Clear ${n.title}`}
                        className="absolute right-1.5 top-1.5 rounded p-1 text-slate-300 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-50 dark:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                        onClick={() => clearOne.mutate(n.id)}
                        disabled={clearOne.isPending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {panel}
    </div>
  );
}
